/**
 * 지도 데이터 한 곳 (DESIGN §8.2 전환 규칙).
 *
 * 캔버스가 데이터를 직접 가져오면 범례·표가 같은 값을 다시 가져와야 하고, 그러면
 * **화면의 색과 표의 숫자가 다른 순간**이 생긴다. 그래서 페이지가 이 훅으로
 * 한 번만 가져와 캔버스·범례·표에 같은 배열을 내려보낸다.
 *
 * 줌 → 표현 전환은 여기서만 판단한다: zoom<8 → r7, 8~11 → r9, ≥11 → 포인트.
 */

import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { fetchEnvelope } from '@/lib/api'
import type { Envelope, H3Data, PointsData } from '@/lib/types'
import { filterKey, toQueryFilters, useFilters } from '@/stores/filters'

export const ZOOM_R9 = 8
export const ZOOM_POINTS = 11

export type MapMetric = 'coverage' | 'availability' | 'utilization' | 'n_chargers'

export const METRICS: { key: MapMetric; label: string; hint: string }[] = [
  {
    key: 'coverage',
    label: '커버리지',
    hint: '관측된 시간의 비율. 0.13~1.00 으로 지역차가 크다 — 지도에서 가장 정보량이 크다',
  },
  {
    key: 'availability',
    label: '가동률',
    hint: '0.98~1.00 에 몰려 있어 구간 경계가 촘촘하다. 경계값을 함께 읽어야 한다',
  },
  {
    key: 'utilization',
    label: '이용률',
    hint: '관측 시간 중 충전중이었던 비중. 「멀쩡한가」가 아니라 「쓰이는가」를 본다 (§8.9)',
  },
  { key: 'n_chargers', label: '충전기 수', hint: '분포 자체. 밀집 지역을 확인할 때' },
]

export interface Cell {
  id: string
  /** 이 셀의 대표 시·도 (H3 는 최빈, 포인트는 정확) — 좌우 분포 막대가 쓴다 */
  zcode: string | null
  lat: number
  lng: number
  n_chargers: number
  availability: number | null
  coverage: number | null
  /** 이용률 — 분모는 관측 시간이다 (§8.9) */
  utilization: number | null
  /** 선택한 지표의 값 — 색을 결정한다 */
  value: number | null
}

export interface ViewState {
  longitude: number
  latitude: number
  zoom: number
  pitch: number
  bearing: number
}

export const KOREA: ViewState = {
  longitude: 127.8,
  latitude: 36.3,
  zoom: 6.2,
  pitch: 0,
  bearing: 0,
}

function pick(metric: MapMetric, c: Omit<Cell, 'value'>): number | null {
  if (metric === 'n_chargers') return c.n_chargers
  if (metric === 'availability') return c.availability
  if (metric === 'utilization') return c.utilization
  return c.coverage
}

export function useGeoData(view: ViewState, metric: MapMetric, bbox: string | undefined) {
  const filters = useFilters()
  const res = view.zoom < ZOOM_R9 ? 7 : 9

  /**
   * **시도를 고르면 줌과 무관하게 포인트를 요청한다** (DESIGN §8.6.9).
   *
   * 이전에는 `zoom < 11` 이면 /geo/points 를 호출조차 안 해서, 제주(8,695기)를
   * 필터해도 전국 줌에서는 헥스만 보였다 — 필터는 걸리는데 화면은 안 좁아지는
   * 상태였다. 16개 시도 중 14개가 POINT_CAP(50,000) 아래이므로 시도 하나를
   * 고르면 그 시도 전체가 개별 포인트로 보이는 것이 맞다.
   *
   * 상한 판단은 서버에 맡긴다 — geo_points_count 로 먼저 세고 초과하면
   * h3_r7 로 강등하며 그 사유를 응답 meta 에 싣는다. 경기(154,291)·서울
   * (75,097)만 강등이 유지되고, 강등 문장은 화면에 이미 나온다.
   */
  const zonePicked = filters.zcode.length > 0
  const wantPoints = view.zoom >= ZOOM_POINTS || zonePicked
  // 시도로 좁혔으면 bbox 를 걸지 않는다 — 뷰포트 밖의 같은 시도도 봐야 한다
  const effBbox = zonePicked ? undefined : bbox

  const hexQ = useQuery({
    queryKey: ['geo/h3', res, filterKey(filters)],
    queryFn: ({ signal }) =>
      fetchEnvelope<H3Data>('/geo/h3', toQueryFilters(filters), { res }, signal),
    staleTime: 5 * 60 * 1000,
    enabled: !wantPoints,
  })

  const pointQ = useQuery({
    queryKey: ['geo/points', effBbox ?? '', filterKey(filters)],
    queryFn: ({ signal }) =>
      fetchEnvelope<PointsData | H3Data>(
        '/geo/points',
        toQueryFilters(filters),
        effBbox ? { bbox: effBbox } : {},
        signal,
      ),
    staleTime: 5 * 60 * 1000,
    enabled: wantPoints && (effBbox != null || zonePicked),
  })

  const active = wantPoints ? pointQ : hexQ
  const env = active.data as Envelope<PointsData | H3Data> | undefined

  const cells = useMemo<Cell[]>(() => {
    if (!env) return []
    // 서버가 상한(5만) 초과로 h3 로 강등해 보냈을 수 있다 (§7.2) — 모양으로 판별한다
    if ('cells' in env.data) {
      const d = env.data
      return d.cells.map((id, i) => {
        const base = {
          id,
          zcode: d.zcode?.[i] ?? null,
          lat: d.lat[i] ?? 0,
          lng: d.lng[i] ?? 0,
          n_chargers: d.n_chargers[i] ?? 0,
          availability: d.availability[i] ?? null,
          coverage: d.coverage[i] ?? null,
          utilization: d.utilization?.[i] ?? null,
        }
        return { ...base, value: pick(metric, base) }
      })
    }
    const d = env.data
    return d.keys.map((id, i) => {
      const base = {
        id,
        zcode: d.zcode?.[i] ?? null,
        lat: d.lat[i] ?? 0,
        lng: d.lng[i] ?? 0,
        n_chargers: 1,
        availability: d.availability[i] ?? null,
        coverage: d.coverage[i] ?? null,
        utilization: d.utilization?.[i] ?? null,
      }
      return { ...base, value: pick(metric, base) }
    })
  }, [env, metric])

  const isPoints = env ? !('cells' in env.data) : wantPoints
  // 두 시·도가 섞인 칸 수 — 최빈 배정의 오차를 화면이 숨기지 않게 한다
  const nMixedZcode =
    env && 'cells' in env.data ? (env.data.n_cells_mixed_zcode ?? 0) : 0

  return {
    cells,
    isPoints,
    nMixedZcode,
    /** 서버가 말한 표현. 강등이 일어났으면 여기서 드러난다 */
    representation: env?.meta.representation ?? (wantPoints ? 'points' : `h3_r${res}`),
    meta: env?.meta,
    isLoading: active.isLoading,
    error: active.error as Error | null,
    refetch: () => void active.refetch(),
  }
}
